import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Container,
    IconButton,
    Paper,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import server from '../../global/server';
import useHandleErrors from '../../hooks/useHandleErrors';

const hora = valor =>
    new Date(valor).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const ROTULO_DO_STATUS = {
    AGUARDANDO: { texto: 'Na fila', cor: 'warning' },
    EM_ANDAMENTO: { texto: 'Em atendimento', cor: 'success' },
    ENCERRADO: { texto: 'Encerrado', cor: 'default' },
};

/**
 * Atendimento pelo canal interno da Plataforma (Termos 1.11).
 *
 * A primeira mensagem é que abre a solicitação — não há formulário de abertura
 * antes de dizer o que se quer. Enquanto ninguém do backoffice assume, o
 * usuário continua podendo escrever: a fila é de atendimento, não de escrita.
 *
 * Tempo real por socket, com uma busca inicial pelo REST. O socket cuida do que
 * chega depois; sem ele a conversa começaria vazia a cada abertura.
 */
const Support = () => {
    const { socket } = useApp();
    // O chatbot manda para ca com ?assunto=denuncia. O tipo so vale na abertura:
    // ele acompanha a primeira mensagem e define como a solicitacao entra na
    // fila do backoffice.
    const [parametros] = useSearchParams();
    const denuncia = parametros.get('assunto') === 'denuncia';

    const [atendimento, setAtendimento] = useState(null);
    const [mensagens, setMensagens] = useState([]);
    const [texto, setTexto] = useState('');
    const [carregando, setCarregando] = useState(true);
    const [enviando, setEnviando] = useState(false);
    const [erro, setErro] = useState();
    useHandleErrors(erro);

    const fimDaLista = useRef(null);

    const carregar = useCallback(async () => {
        try {
            const { data } = await server.get('/suporte');
            setAtendimento(data.data?.ticket || null);
            setMensagens(data.data?.messages || []);
        } catch (falha) {
            setErro(falha);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => {
        carregar();
    }, [carregar]);

    useEffect(() => {
        fimDaLista.current?.scrollIntoView({ behavior: 'smooth' });
    }, [mensagens]);

    useEffect(() => {
        if (!socket) return;

        const aoReceber = ({ message }) => {
            // Duas abas abertas devolvem a propria mensagem: o filtro por id
            // evita que ela apareca duas vezes
            setMensagens(atual => (atual.some(item => item.id === message.id) ? atual : [...atual, message]));
        };
        const aoAtualizar = ({ ticket }) => setAtendimento(ticket);

        socket.on('suporte:mensagem', aoReceber);
        socket.on('suporte:atualizado', aoAtualizar);

        return () => {
            socket.off('suporte:mensagem', aoReceber);
            socket.off('suporte:atualizado', aoAtualizar);
        };
    }, [socket]);

    const enviar = async () => {
        const corpo = texto.trim();
        if (!corpo || enviando) return;

        try {
            setEnviando(true);
            const { data } = await server.post('/suporte/mensagem', {
                body: corpo,
                ...(denuncia && !atendimento ? { kind: 'DENUNCIA' } : {}),
            });
            setTexto('');
            setMensagens(atual =>
                atual.some(item => item.id === data.data.id) ? atual : [...atual, data.data],
            );
            // A primeira mensagem cria a solicitação: sem recarregar, a tela não
            // saberia que agora existe um atendimento na fila
            if (!atendimento) carregar();
        } catch (falha) {
            setErro(falha);
        } finally {
            setEnviando(false);
        }
    };

    const status = ROTULO_DO_STATUS[atendimento?.status];

    return (
        <Container maxWidth="sm" sx={{ py: 3 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h6" fontWeight={700}>
                    Atendimento
                </Typography>
                {status && <Chip size="small" label={status.texto} color={status.cor} />}
            </Stack>

            {!atendimento && mensagens.length > 0 && (
                <Alert severity="info" sx={{ borderRadius: '12px', mb: 2 }}>
                    Nenhum atendimento aberto. Escrever abaixo abre uma nova solicitação.
                </Alert>
            )}

            {denuncia && !atendimento && (
                <Alert severity="warning" sx={{ borderRadius: '12px', mb: 2 }}>
                    Você está abrindo uma <strong>denúncia</strong>. Conte o que aconteceu e, se puder,
                    informe o nome do encontro e a data.
                </Alert>
            )}

            {atendimento?.status === 'AGUARDANDO' && (
                <Alert severity="info" sx={{ borderRadius: '12px', mb: 2 }}>
                    Sua solicitação está na fila. Assim que alguém da equipe assumir, a conversa continua
                    por aqui — pode escrever à vontade enquanto isso.
                </Alert>
            )}

            {atendimento?.status === 'EM_ANDAMENTO' && atendimento.admin?.name && (
                <Alert severity="success" sx={{ borderRadius: '12px', mb: 2 }}>
                    {atendimento.admin.name} está atendendo você.
                </Alert>
            )}

            <Paper
                variant="outlined"
                sx={{
                    borderRadius: '16px',
                    p: 2,
                    height: '55vh',
                    overflowY: 'auto',
                    bgcolor: '#fafafa',
                }}
            >
                {carregando ? (
                    <Stack alignItems="center" sx={{ py: 4 }}>
                        <CircularProgress size={24} />
                    </Stack>
                ) : mensagens.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
                        Escreva a sua dúvida abaixo. A primeira mensagem abre o atendimento.
                    </Typography>
                ) : (
                    <Stack spacing={1}>
                        {mensagens.map(mensagem => {
                            // Aviso do sistema nao e fala de ninguem: em vez de
                            // balao de um lado, ocupa a faixa inteira e marca
                            // onde um atendimento terminou e o proximo comeca
                            if (mensagem.sender === 'SISTEMA') {
                                return (
                                    <Box
                                        key={mensagem.id}
                                        sx={{
                                            alignSelf: 'stretch',
                                            width: '100%',
                                            bgcolor: '#fff8e1',
                                            border: '1px solid #ffe0b2',
                                            borderRadius: '10px',
                                            px: 2,
                                            py: 1.5,
                                            my: 1,
                                        }}
                                    >
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#8a5a00', display: 'block', lineHeight: 1.5 }}
                                        >
                                            {mensagem.body}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            sx={{ color: '#8a5a00', opacity: 0.7, display: 'block', textAlign: 'right' }}
                                        >
                                            {hora(mensagem.createdAt)}
                                        </Typography>
                                    </Box>
                                );
                            }

                            const meu = mensagem.sender === 'USUARIO';
                            return (
                                <Box
                                    key={mensagem.id}
                                    sx={{
                                        alignSelf: meu ? 'flex-end' : 'flex-start',
                                        maxWidth: '80%',
                                        bgcolor: meu ? '#006a61' : 'white',
                                        color: meu ? 'white' : 'text.primary',
                                        border: meu ? 'none' : '1px solid #e8e8e8',
                                        borderRadius: '12px',
                                        px: 1.5,
                                        py: 1,
                                    }}
                                >
                                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                        {mensagem.body}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ opacity: 0.7, display: 'block', textAlign: 'right' }}
                                    >
                                        {hora(mensagem.createdAt)}
                                    </Typography>
                                </Box>
                            );
                        })}
                        <div ref={fimDaLista} />
                    </Stack>
                )}
            </Paper>

            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    placeholder="Escreva a sua mensagem"
                    value={texto}
                    onChange={evento => setTexto(evento.target.value)}
                    onKeyDown={evento => {
                        // Enter envia, Shift+Enter quebra linha
                        if (evento.key === 'Enter' && !evento.shiftKey) {
                            evento.preventDefault();
                            enviar();
                        }
                    }}
                />
                <IconButton
                    onClick={enviar}
                    disabled={!texto.trim() || enviando}
                    sx={{ bgcolor: '#006a61', color: 'white', '&:hover': { bgcolor: '#005a52' } }}
                >
                    <SendIcon />
                </IconButton>
            </Stack>
        </Container>
    );
};

export default Support;
